import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { axiosInstance } from 'shared/api/axios'

export const useLoginStatusQuery = () => {
  const { enqueueSnackbar } = useSnackbar()

  const query = useQuery({
    queryKey: ['login-status'],
    staleTime: Infinity,
    queryFn: async () => {
      try {
        const response = await axiosInstance.get<1 | 0>(
          '/user/login_status?_format=json'
        )
        return response.data
      } catch (e) {
        enqueueSnackbar('Login status fetching error', {
          variant: 'error',
        })
        throw new Error('Login status fetching error', {
          cause: e,
        })
      }
    },
    retry: 1,
  })

  return query
}

interface LoginParams {
  name: string
  pass: string
}

interface LoginResponse {
  logout_token: string
}

export const useLoginMutation = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  const mutation = useMutation({
    mutationKey: ['login'],
    mutationFn: async (params: LoginParams) => {
      const response = await axiosInstance.post<LoginResponse>(
        '/user/login?_format=json',
        params
      )
      return response.data
    },
    onSuccess: (data) => {
      localStorage.setItem('logoutToken', data.logout_token)

      queryClient.setQueryData(['login-status'], 1)
    },
    onError: () => {
      enqueueSnackbar('Login failed', {
        variant: 'error',
      })
    },
  })

  return mutation
}

export const useLogoutMutation = () => {
  const { enqueueSnackbar } = useSnackbar()

  const logoutToken = localStorage.getItem('logoutToken')

  const mutation = useMutation({
    mutationKey: ['logout'],
    mutationFn: () =>
      axiosInstance.post(`/user/logout?_format=json&token=${logoutToken}`),
    onSuccess: () => {
      window.location.reload()
    },
    onError: () => {
      enqueueSnackbar('Log out failed', {
        variant: 'error',
      })
    },
  })

  return mutation
}
